<template>
  <div>
    <v-row no-gutters>
      <v-col>
        <i18n-t keypath="projects.dialogs.ProjectTypeQuoteRequestDialog.text" tag="p" scope="global">
          <template #phoneNumber>
            <a :href="`tel:${SALES_PHONE_NUMBER_WITH_COUNTRY_CODE}`" class="text-text-link ">{{ SALES_PHONE_NUMBER }}</a>
          </template>
        </i18n-t>

        <fieldset class="mt-6">
          <h3 class="form-label required-asterisk">
            {{ $t('projects.ProjectsDataroomContactForm.phoneNumber') }}
          </h3>
          <AppPhoneField :value="telephone"
                         :error-messages="phoneNumberErrors"
                         @on-input="handlePhoneChange"
          />
        </fieldset>

        <fieldset class="mt-4">
          <h3 class="form-label required-asterisk">
            {{ $t('projects.ProjectsDataroomContactForm.offer') }}
          </h3>
          <AppSelect
            v-model="v$.offer.$model"
            :items="offers"
            item-title="text"
            no-label
            :placeholder="$t('projects.ProjectsDataroomContactForm.offerPlaceholder')"
            :error-messages="offerErrors"
          />
        </fieldset>

        <fieldset class="mt-4">
          <h3 class="form-label">
            {{ $t('projects.ProjectsDataroomContactForm.requestDetails') }}
          </h3>
          <v-textarea
            v-model="requestDetails"
            class="request-details-field"
            hide-details
            color="border-info-bold"
            variant="outlined"
            :placeholder="$t('projects.ProjectsDataroomContactForm.requestDetailsPlaceholder')"
            rows="4"
          />
        </fieldset>

        <v-checkbox v-model="v$.isPrivacyPolicyCheck.$model"
                    :error-messages="termsCheckedError"
                    :hide-details="termsCheckedError.length === 0"
                    class="privacy-policy-check mt-6"
        >
          <template #label>
            <i18n-t keypath="projects.dialogs.ProjectTypeQuoteRequestDialog.privacyPolicyConsentText" tag="p" class="mb-0" scope="global">
              <template #privacyPolicyUrl>
                <a :href="$t('projects.dialogs.ProjectTypeQuoteRequestDialog.privacyPolicyUrl')"
                   target="_blank"
                   class="text-darkblue-500 d-inline-block"
                   @click.stop
                >
                  {{$t('projects.dialogs.ProjectTypeQuoteRequestDialog.privacyPolicy')}}
                </a>
              </template>
            </i18n-t>
          </template>
        </v-checkbox>
      </v-col>
    </v-row>

    <!-- Exists purely to avoid reloading after submitting the form... -->
    <iframe class="d-none" name="hiddenFrame" ref="formIframe" width="0" height="0" title="hidden-contact-form-iframe"></iframe>
    <form
      ref="quoteRequestForm"
      class="d-none"
      :action="QUOTE_REQUEST_TOPICS_ENDPOINTS['DR']"
      method="post"
      target="hiddenFrame"
    >
      <input type="hidden" name="comments" :value="requestDetails" />
      <input type="hidden" name="phone" :value="v$.telephone.$model" />
      <input type="hidden" name="offer" :value="v$.offer.$model" />
      <input type="hidden" name="email" :value="profile.email" />
      <input type="hidden" name="firstname" :value="profile.firstName" />
      <input type="hidden" name="lastname" :value="profile.lastName" />
      <input type="hidden" name="orgname" :value="profile.company" />
      <input
        v-if="profile.subscription?.country"
        type="hidden" name="country"
        :value="profile.subscription.country"
      />
    </form>
  </div>

</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, sameAs } from '@vuelidate/validators'
import { parsePhoneNumber } from 'awesome-phonenumber'
import { mapState } from 'vuex'

import AppPhoneField from '@/common/AppPhoneField.vue'
import AppSelect from '@/common/AppSelect.vue'
import { SALES_PHONE_NUMBER, SALES_PHONE_NUMBER_WITH_COUNTRY_CODE } from '@/common/utils/closdInfo.js'
import { PPU_DATAROOM_BASE_SIZE_OPTIONS } from '@/common/utils/datarooms.ts'
import { formFieldErrors } from '@/common/utils/form.ts'
import { QUOTE_REQUEST_TOPICS_ENDPOINTS } from '@/common/utils/quoteRequestTopics.js'
import { toast } from '@/common/utils/toast.ts'

// The form is submitted to the iframe since it's a standard submission,
// so we need to poll it to check if the form was successfully submitted
const FORM_SUCCESS_POLLING_INTERVAL = 50

export default {
  name: 'ProjectsDataroomContactForm',
  components: { AppSelect, AppPhoneField },
  setup: () => ({ v$: useVuelidate() }),
  validations () {
    return {
      isPrivacyPolicyCheck: {
        required,
        sameAs: sameAs(true),
      },
      telephone: { required, isValidPhoneNumber: this.isValidPhoneNumber },
      offer: {
        required,
      },
    }
  },
  data () {
    return {
      SALES_PHONE_NUMBER,
      SALES_PHONE_NUMBER_WITH_COUNTRY_CODE,
      QUOTE_REQUEST_TOPICS_ENDPOINTS,
      telephone: '',
      offer: null,
      requestDetails: null,
      isPrivacyPolicyCheck: false,
      quoteRequestFormIntervalTimerId: -1,
    }
  },
  computed: {
    ...mapState('user', ['profile']),
    phoneNumberErrors () {
      return formFieldErrors(
        this.v$.telephone,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('common.phoneNumber') })],
          ['isValidPhoneNumber', this.$t('projects.ProjectsDataroomContactForm.phoneNumberInvalid')],
        ],
      )
    },
    offerErrors () {
      return formFieldErrors(
        this.v$.offer,
        [
          ['required', this.$t('projects.ProjectsDataroomContactForm.offerRequired')],
        ],
      )
    },
    termsCheckedError () {
      return formFieldErrors(
        this.v$.isPrivacyPolicyCheck,
        [
          ['sameAs', this.$t('projects.ProjectsDataroomContactForm.termsCheckedValidationError')],
        ],
      )
    },
    offers () {
      const dataroomBaseSizeOffers = PPU_DATAROOM_BASE_SIZE_OPTIONS.map((sizeOption) => {
        return {
          value: `${sizeOption.value} ${sizeOption.unit.toUpperCase()}`,
          text: `${sizeOption.value} ${this.$t('projects.ProjectsDataroomContactForm.sizeUnit.' + sizeOption.unit)}`,
        }
      })
      return [...dataroomBaseSizeOffers, {
        value: 'Other',
        text: this.$t('projects.ProjectsDataroomContactForm.other'),
      }]
    },
  },
  created () {
    this.telephone = this.profile.telephone
  },
  beforeUnmount () {
    if (this.quoteRequestFormIntervalTimerId !== -1) {
      clearInterval(this.quoteRequestFormIntervalTimerId)
    }
  },
  methods: {
    isValidPhoneNumber (phoneNumber) {
      if (!phoneNumber) {
        return true // We let the "required" validator handle empty phone numbers
      }

      const parsingResult = parsePhoneNumber(phoneNumber)
      return parsingResult.valid
    },
    async sendRequest () {
      this.v$.$touch()
      const isFormValid = !this.v$.$invalid
      if (!isFormValid) {
        return false
      }

      try {
        this.$refs.quoteRequestForm.submit()
        this.quoteRequestPending = true
        this.quoteRequestFormIntervalTimerId = setInterval(() => {
          // If the iframe is trying to reload the page, the form has been submitted
          if (this.$refs.formIframe.contentWindow.location.hostname === globalThis.location.hostname) {
            clearInterval(this.quoteRequestFormIntervalTimerId)
            this.onFormSubmitSuccess()
          }
        }, FORM_SUCCESS_POLLING_INTERVAL)

        // Just in case, we set an onload handler to the iframe
        // When it "loads", it means the form was submit and has finished redirecting,
        // So it is done. This unfortunately means we may wait until
        // The newly booted app from the redirection is fully loaded
        this.$refs.formIframe.onload = this.onFormSubmitSuccess

      } catch (error) {
        console.error(error)
        toast.error(this.$t('projects.dialogs.ProjectTypeQuoteRequestDialog.quoteRequestErrorMessage'))
        return false
      }

      return true
    },
    onFormSubmitSuccess () {
      this.$emit('formSubmitSuccess')
    },
    handlePhoneChange (input, event) {
      this.telephone = event.number || input
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/typography-tokens";
@import "@/scss/border-tokens";

fieldset {
  border: none;
}

.form-label {
  @include text('heading-xs-bold');
  margin-bottom: 8px;
  color: rgb(var(--v-theme-text-primary));
}

.privacy-policy-check {
  padding-top: 4px;

  :deep(.v-selection-control) {
    align-items: flex-start;
  }
}

.request-details-field {
  --outline-color: rgb(var(--v-theme-border-secondary));
  --outline-width: #{get-width('border-width-1')};
  --text-color: rgb(var(--v-theme-text-secondary));

  &:focus-within {
    --outline-color: rgb(var(--v-theme-border-info-bold));
    --outline-width: #{get-width('border-width-2')};
  }

  &.v-input--error {
    --outline-color: rgb(var(--v-theme-border-danger-bold)) !important;
  }

  :deep(.v-field__input)::placeholder {
    color: rgb(var(--v-theme-text-placeholder));
    opacity: 1;
  }

  :deep(.v-field__outline__start),
  :deep(.v-field__outline__end), {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__notch::before) {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__notch::after) {
    border-color: var(--outline-color);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__start) {
    border-left-width: var(--outline-width);
  }

  :deep(.v-field__outline__end) {
    border-right-width: var(--outline-width);
  }

  &.v-input--error :deep(.v-messages) {
    color: rgb(var(--v-theme-text-danger)) !important;
  }
}

</style>
